/**
 * VisualizerPanel — the ONE shared visualizer panel for every editor. Reads the
 * shared vizStore, so Synth VR / Flow / Console all render the exact same UI:
 *   • On / Off
 *   • React to: Audio (live amplitude) or Notes (chart notes crossing the head)
 *   • Camera: Auto (aligns to the highway) or Manual (pan X/Y + zoom Z)
 *   • Library (Scenes / Projects) — click to activate, × to remove
 *   • Import a .visualizer / .vizproject
 */

import React, { useEffect, useRef } from 'react';
import { useVizStore, VIZ_ZOOM_MIN, VIZ_ZOOM_MAX } from './vizStore';
import { useTheme } from '../components/ui/themeStore';
import GradientSlider from '../components/ui/GradientSlider';
import { color, font } from '../components/ui/theme';

export default function VisualizerPanel() {
  const project = useVizStore(s => s.project);
  const enabled = useVizStore(s => s.enabled);
  const library = useVizStore(s => s.library);
  const activeId = useVizStore(s => s.activeId);
  const reactTo = useVizStore(s => s.reactTo);
  const camMode = useVizStore(s => s.cameraMode);
  const camX = useVizStore(s => s.camX);
  const camY = useVizStore(s => s.camY);
  const camZ = useVizStore(s => s.camZ);
  const setEnabled = useVizStore(s => s.setEnabled);
  const setReactTo = useVizStore(s => s.setReactTo);
  const setCameraMode = useVizStore(s => s.setCameraMode);
  const setCam = useVizStore(s => s.setCam);
  const syncCam = useVizStore(s => s.syncCam);
  const importFile = useVizStore(s => s.importFile);
  const activate = useVizStore(s => s.activate);
  const remove = useVizStore(s => s.remove);
  const restore = useVizStore(s => s.restore);
  const fileRef = useRef<HTMLInputElement>(null);

  const activeEditor = useTheme(s => s.activeEditor);

  useEffect(() => { void restore(); }, [restore]);

  // Reload the saved camera position whenever the active visual or editor changes.
  useEffect(() => { syncCam(activeEditor); }, [activeId, activeEditor, syncCam]);

  const hasViz = !!project;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {/* On / Off */}
      <button
        onClick={() => setEnabled(!enabled)}
        disabled={!hasViz}
        style={{ ...btn, justifyContent: 'center', ...(enabled && hasViz ? btnOn : null), opacity: hasViz ? 1 : 0.5 }}
      >
        {enabled && hasViz ? 'Visualizer: On' : 'Visualizer: Off'}
      </button>

      {/* React to */}
      <div style={segStyle}>
        <span style={segLabelStyle}>React to</span>
        {(['audio', 'notes'] as const).map(m => (
          <button key={m} onClick={() => setReactTo(m)} style={{ ...segBtnStyle, ...(reactTo === m ? segBtnActiveStyle : null) }}>
            {m === 'audio' ? 'Audio' : 'Notes'}
          </button>
        ))}
      </div>

      {/* Camera */}
      <div style={segStyle}>
        <span style={segLabelStyle}>Camera</span>
        {(['auto', 'manual'] as const).map(m => (
          <button key={m} onClick={() => setCameraMode(m)} style={{ ...segBtnStyle, ...(camMode === m ? segBtnActiveStyle : null) }}>
            {m === 'auto' ? 'Auto' : 'Manual'}
          </button>
        ))}
      </div>

      {camMode === 'manual' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <GradientSlider inline label="X" valueText={String(camX)}
            value={(camX + 50) / 100}
            onChange={v => { const x = Math.round(v * 100 - 50); setCam({ x: Math.abs(x) <= 5 ? 0 : x }, activeEditor); }} />
          <GradientSlider inline label="Y" valueText={String(camY)}
            value={(camY + 50) / 100}
            onChange={v => { const y = Math.round(v * 100 - 50); setCam({ y: Math.abs(y) <= 5 ? 0 : y }, activeEditor); }} />
          <GradientSlider inline label="Z" valueText={camZ.toFixed(2)}
            value={(camZ - VIZ_ZOOM_MIN) / (VIZ_ZOOM_MAX - VIZ_ZOOM_MIN)}
            onChange={v => setCam({ z: Math.round((VIZ_ZOOM_MIN + v * (VIZ_ZOOM_MAX - VIZ_ZOOM_MIN)) * 100) / 100 }, activeEditor)} />
        </div>
      )}

      {/* Library */}
      {(['scene', 'project'] as const).map(kind => {
        const items = library.filter(v => v.kind === kind);
        if (items.length === 0) return null;
        return (
          <div key={kind} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={subLabelStyle}>{kind === 'scene' ? 'Scenes' : 'Projects'}</div>
            {items.map(it => {
              const active = it.id === activeId;
              return (
                <div key={it.id} style={{ ...chipStyle, ...(active ? chipActiveStyle : null) }}>
                  <button onClick={() => { void activate(it.id); }} style={{ ...chipNameStyle, color: active ? 'var(--accent)' : color.inkDim }} title={it.name}>
                    {it.name}
                  </button>
                  <button onClick={() => remove(it.id)} style={chipRemoveStyle} aria-label="Remove visual" title="Remove">✕</button>
                </div>
              );
            })}
          </div>
        );
      })}

      {/* Import */}
      <button onClick={() => fileRef.current?.click()} style={{ ...btn, justifyContent: 'center' }}>
        {library.length ? 'Import another…' : 'Import .visualizer…'}
      </button>
      <input
        ref={fileRef}
        type="file"
        accept=".visualizer,.vizproject,application/json"
        style={{ display: 'none' }}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) importFile(f); e.target.value = ''; }}
      />
    </div>
  );
}

const btn: React.CSSProperties = {
  appearance: 'none', display: 'flex', alignItems: 'center',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.25)',
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.4)',
  color: color.ink, fontFamily: font.sans, fontSize: 12, fontWeight: 600,
  borderRadius: 999, padding: '8px 10px', cursor: 'pointer',
};
const btnOn: React.CSSProperties = {
  background: 'linear-gradient(135deg, oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h)/0.25), oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h)/0.15))',
  borderColor: 'oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h)/0.5)', color: 'var(--accent)',
};
const segStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 4 };
const segLabelStyle: React.CSSProperties = { fontFamily: font.sans, fontSize: 11, color: color.inkDim, marginRight: 4, flexShrink: 0 };
const segBtnStyle: React.CSSProperties = {
  flex: 1, appearance: 'none', border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.18)',
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.4)', color: color.accentSoft,
  fontFamily: font.sans, fontSize: 11, fontWeight: 600, borderRadius: 999, padding: '5px 8px', cursor: 'pointer',
};
const segBtnActiveStyle: React.CSSProperties = {
  background: 'oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h)/0.18)', borderColor: 'var(--accent)', color: 'var(--accent)',
};
const subLabelStyle: React.CSSProperties = {
  fontFamily: font.sans, fontSize: 10, fontWeight: 700, letterSpacing: 0.4,
  textTransform: 'uppercase', color: color.inkFaint, marginTop: 2,
};
const chipStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 999,
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h)/0.18)', background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h)/0.4)',
};
const chipActiveStyle: React.CSSProperties = {
  borderColor: 'oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h)/0.5)', background: 'oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h)/0.12)',
};
const chipNameStyle: React.CSSProperties = {
  flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  textAlign: 'left', background: 'transparent', border: 'none', cursor: 'pointer',
  fontFamily: font.sans, fontSize: 11, padding: 0,
};
const chipRemoveStyle: React.CSSProperties = {
  appearance: 'none', border: 'none', background: 'transparent', color: color.inkFaint,
  cursor: 'pointer', fontSize: 11, padding: 2, flexShrink: 0,
};
